import type { PublicUser, UserCreate, UserListResponse, UserRole, UserUpdate } from '@messe/shared'
import { apiRequest } from './api.js'

export interface UserListParams {
  search?: string
  role?: UserRole
  includeInactive?: boolean
  limit?: number
  offset?: number
}

export function listUsers(params: UserListParams = {}): Promise<UserListResponse> {
  const query = new URLSearchParams()
  if (params.search) query.set('search', params.search)
  if (params.role) query.set('role', params.role)
  if (params.includeInactive) query.set('includeInactive', 'true')
  if (params.limit !== undefined) query.set('limit', String(params.limit))
  if (params.offset !== undefined) query.set('offset', String(params.offset))

  const suffix = query.size > 0 ? `?${query.toString()}` : ''
  return apiRequest<UserListResponse>(`/users${suffix}`)
}

export function createUser(input: UserCreate): Promise<PublicUser> {
  return apiRequest<PublicUser>('/users', { method: 'POST', body: input })
}

export function updateUser(id: string, changes: UserUpdate): Promise<PublicUser> {
  return apiRequest<PublicUser>(`/users/${id}`, { method: 'PATCH', body: changes })
}

export async function resetUserPassword(
  id: string,
  password: string,
  revokeSessions: boolean,
): Promise<void> {
  // The endpoint answers 204, so there is nothing to return.
  await apiRequest<undefined>(`/users/${id}/password`, {
    method: 'POST',
    body: { password, revokeSessions },
  })
}

/**
 * Generates a passphrase for a new or reset account.
 *
 * Random words rather than random characters: the result has to survive being
 * read aloud or typed on a phone keyboard, and four words from this list carry
 * more entropy than the eight-character jumble people actually write down.
 */
const WORDS = [
  'anker',
  'birke',
  'delta',
  'eiche',
  'falke',
  'garten',
  'hafen',
  'insel',
  'jaeger',
  'kompass',
  'lampe',
  'muehle',
  'nadel',
  'orkan',
  'pfeil',
  'quelle',
  'regen',
  'segel',
  'turm',
  'ufer',
  'vogel',
  'wolke',
  'zeder',
] as const

export function generatePassphrase(): string {
  const values = new Uint32Array(5)
  crypto.getRandomValues(values)

  const words = Array.from(values.slice(0, 4), (value) => WORDS[value % WORDS.length] ?? WORDS[0])
  const digits = String((values[4] ?? 0) % 100).padStart(2, '0')

  return `${words.join('-')}-${digits}`
}
